import { ScrollableTabsList } from '@workspace/ui/components/scrollable-tabs'
import { Tabs, TabsContent, TabsTrigger } from '@workspace/ui/components/tabs'
import { AlertTriangle, Lightbulb, Rocket, Shield } from 'lucide-react'
import { MarkdownContent } from '@/components/markdown-content'
import type { Evaluation } from '../types/pitch.types'

interface PitchAnalysisTabsProps {
  evaluation: Evaluation
}

export function PitchAnalysisTabs({ evaluation }: PitchAnalysisTabsProps) {
  const hasThesis = !!evaluation.winnerPersonaThesis
  const hasTensions = !!evaluation.tensions
  const hasRisks = !!evaluation.risks
  const hasInsights = !!evaluation.actionableInsights

  if (!hasThesis && !hasTensions && !hasRisks && !hasInsights) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-muted-foreground">
        No evaluation analysis available yet.
      </div>
    )
  }

  const defaultTab = hasThesis
    ? 'thesis'
    : hasTensions
      ? 'tensions'
      : hasRisks
        ? 'risks'
        : 'insights'

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <ScrollableTabsList gradientFrom="background">
        {hasThesis && (
          <TabsTrigger value="thesis" className="gap-2">
            <Lightbulb className="size-4 text-emerald-400" />
            <span>Investment Thesis</span>
          </TabsTrigger>
        )}
        {hasTensions && (
          <TabsTrigger value="tensions" className="gap-2">
            <AlertTriangle className="size-4 text-amber-400" />
            <span>Key Points</span>
          </TabsTrigger>
        )}
        {hasRisks && (
          <TabsTrigger value="risks" className="gap-2">
            <Shield className="size-4 text-red-400" />
            <span>Risks</span>
          </TabsTrigger>
        )}
        {hasInsights && (
          <TabsTrigger value="insights" className="gap-2">
            <Rocket className="size-4 text-blue-400" />
            <span>Actionable Insights</span>
          </TabsTrigger>
        )}
      </ScrollableTabsList>

      {hasThesis && (
        <TabsContent value="thesis" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <MarkdownContent>{evaluation.winnerPersonaThesis}</MarkdownContent>
          </div>
        </TabsContent>
      )}

      {hasTensions && (
        <TabsContent value="tensions" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <MarkdownContent>{evaluation.tensions}</MarkdownContent>
          </div>
        </TabsContent>
      )}

      {hasRisks && (
        <TabsContent value="risks" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <MarkdownContent>{evaluation.risks}</MarkdownContent>
          </div>
        </TabsContent>
      )}

      {hasInsights && (
        <TabsContent value="insights" className="mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <MarkdownContent>{evaluation.actionableInsights}</MarkdownContent>
          </div>
        </TabsContent>
      )}
    </Tabs>
  )
}
